import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cond from 'lodash/cond';
import size from 'lodash/size';
import toLower from 'lodash/toLower';
import classnames from 'classnames';

import { SparkTable } from '../../../../ui/spark';
import { LinkIcon } from '../../../../assets/LinkIcon';
import ReactSparkModal from '../../../../../components/helpers/CTModal';
import { SAVE_LEVEL } from '../../../../../constants/Level';
import { DOMAIN_ERROR_CODES } from '../../../../../constants/manageDomains';
import { PopoverEx } from '../../../../ui/sabre-spark-react-ex/index';
import { domainValidator } from '../../utils';

import './styles.scss';
import InputTextField from 'components/common/InputTextField';

export class ManageDomains extends Component {
    state = {
        openAddDomainModal: false,
        openVersionHistoryModal: false,
        openRemoveDomainModal: false,
        inputData: '',
        selectedDomainToRemove: null,
        domainValidationErrorCode: '',
        selectedTab: 'hosted'
    };
    popoverRef = null;

    componentDidMount() {
        const { chainId, hotelId, selectedLevel, onPageLoad } = this.props;

        onPageLoad(chainId, hotelId, selectedLevel);
        new SparkTable(this.themeTableMount);
    }

    removeDomain = () => {
        const { cspConfiguration, saveLocalChanges, thirdparty } = this.props;
        const { selectedDomainToRemove, selectedTab } = this.state;
        const domainListKey = selectedTab === 'hosted' ? 'listOfDefaultDomains' : 'listOfInContextHostDomains';
        const domains = get(thirdparty, `data.${cspConfiguration}.${domainListKey}`, []);
        const data = domains.filter(item => item !== selectedDomainToRemove);

        saveLocalChanges(data, domainListKey);
    };

    saveAddDomainModalAction = () => {
        const { cspConfiguration, saveLocalChanges, thirdparty } = this.props;
        const { inputData, selectedTab } = this.state;
        const domainListKey = selectedTab === 'hosted' ? 'listOfDefaultDomains' : 'listOfInContextHostDomains';
        const domains = get(thirdparty, `data.${cspConfiguration}.${domainListKey}`, []);
        const domainValidationErrorCode = domainValidator(domains, inputData);
        const hasDomainValidationErrorCode = size(domainValidationErrorCode) >= 1;
        const newInputData = !hasDomainValidationErrorCode ? '' : inputData;

        if (!hasDomainValidationErrorCode) {
            saveLocalChanges([...domains, inputData], domainListKey);
        }

        this.setState({
            inputData: newInputData,
            openAddDomainModal: hasDomainValidationErrorCode,
            domainValidationErrorCode
        });
    };

    removeDomainModalAction = () => {
        this.removeDomain();
        this.setState({ openRemoveDomainModal: false });
    };

    cancelModalAction = () =>
        this.setState({
            openAddDomainModal: false,
            openRemoveDomainModal: false,
            inputData: '',
            domainValidationErrorCode: ''
        });

    closeVersionHistoryModalAction = () => this.setState({ openVersionHistoryModal: false });

    handleAddDomainClick = () => this.setState({ openAddDomainModal: true });

    handleVersionHistoryClick = () => this.setState({ openVersionHistoryModal: true });

    handleRemoveDomainClick = selectedDomainToRemove => () =>
        this.setState({ selectedDomainToRemove, openRemoveDomainModal: true });

    handleOnChangeAddDomain = inputData => {
        const transformedInputData = toLower(inputData);
        this.setState({
            inputData: transformedInputData,
            domainValidationErrorCode: ''
        });
    };

    onTabChange = tabName => {
        this.setState({
            selectedTab: tabName
        });
    };

    getDomainErrorMessage = errorCode => {
        return cond([
            [() => errorCode === DOMAIN_ERROR_CODES.EMPTY, () => 'This field is required'],
            [() => errorCode === DOMAIN_ERROR_CODES.DUPLICATED, () => 'Domain already exists'],
            [
                () => errorCode === DOMAIN_ERROR_CODES.INVALID_WILDCARD_DOMAIN,
                () => 'Domains should be specific. "*" cannot be used in place of a domain or as a root level folder'
            ],
            [
                () => errorCode === DOMAIN_ERROR_CODES.INVALID_DOMAIN,
                () =>
                    "Invalid domain. Domains can be entered with or without protocol (eg. https://domain.com, https://*.domain.com, wss://domain.com, ws://domain.com, domain.com, www.domain.com, *.domain.com) but shouldn't include full paths."
            ]
        ])();
    };

    render() {
        const { thirdparty, selectedLevel, inContextBeta } = this.props;
        const { domainValidationErrorCode, openAddDomainModal, inputData, selectedTab } = this.state;
        const domainListKey = selectedTab === 'hosted' ? 'listOfDefaultDomains' : 'listOfInContextHostDomains';
        const chainDomains = get(thirdparty, `data.chainCspConfiguration.${domainListKey}`, []);
        const hotelDomains = get(thirdparty, `data.hotelCspConfiguration.${domainListKey}`, []);
        const chainActionsClasses = classnames('spark-icon-trash', { disabled: selectedLevel === SAVE_LEVEL.HOTEL });

        return (
            <div
                className="allow-domain-content"
                ref={node => {
                    this.themeTableMount = node;
                }}
            >
                {inContextBeta ? (
                    <div className="spark-tabs">
                        <nav className="spark-tabs__nav" role="menubar">
                            <div className="spark-tabs__scroll">
                                <ul className="spark-tabs__list" role="tablist">
                                    <li
                                        className={`spark-tabs__tab text-center ${this.state.selectedTab === "hosted" ? "active" : ""}`}
                                        role="tab"
                                        onClick={() => this.onTabChange('hosted')}
                                    >
                                        <a>Allowed Domains</a>
                                    </li>
                                    <li
                                        className={`spark-tabs__tab text-center ${this.state.selectedTab === "in-context" ? "active" : ""}`}
                                        role="tab"
                                        onClick={() => this.onTabChange('in-context')}
                                    >
                                        <a>In-context</a>
                                    </li>
                                </ul>
                            </div>
                        </nav>
                        <div className="spark-tabs__panels">
                            <div role="tabpanel" className={`spark-tabs__panel ${selectedTab === "hosted" ? "active" : ""}`} id="hosted-tab">
                                <div className="allow-domain-content-header-label">
                                    <p className="in-context-">
                                        Allow Booking Engine access to the following content domains. Include your brand domain and any
                                        font libraries (eg. Adobe or Google) that use scripts. Specify Internet hosts by name (or IP
                                        address). The site's address may include an optional leading wildcard (the asterisk character,
                                        '*').
                                        <br />
                                        Examples:
                                        <br />
                                        &nbsp;&nbsp;https://*.example.com: Matches all attempts to load from any subdomain of
                                        example.com using https:.
                                        <br />
                                        &nbsp;&nbsp;https://scripts.example.com: Matches all attempts to access scripts.example.com
                                        using https:.
                                        <br />
                                        <br />
                                        Note that *.example.com does not match the top domain example.com. To match top domains use
                                        example.com without the leading asterisk and dot.
                                        <br />
                                        Also note that attempting to load from domains that use the insecure http protocol will result
                                        in mixed content errors, which may disrupt your site.
                                    </p>
                                    <PopoverEx
                                        ref={node => (this.popoverRef = node)}
                                        className="col-xs-4"
                                        toggleEl={<i className="spark-icon-info-circle spark-icon--md" />}
                                    >
                                        <div className="row">
                                            <div className="col-xs-12">
                                                <h4>What's This?</h4>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-xs-12">
                                                <p>
                                                    For security purposes, we block external domains not listed as "allowed". Blocked
                                                    domains can effect site features that rely on calls to external domains such as
                                                    custom fonts, scripts, form actions (AJAX, WebSocket and EvenSource) and external
                                                    style sheets.
                                                </p>
                                            </div>
                                        </div>
                                    </PopoverEx>
                                </div>
                                <div className="header">
                                    <div className="group">
                                        {this.props.hasAccess && (
                                            <button
                                                className="spark-btn spark-btn--sm spark-btn--secondary"
                                                onClick={this.handleAddDomainClick}
                                            >
                                                Add domain
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {thirdparty.isLoading && <div className="ring-loader" />}
                                {thirdparty.isError && <p>Cannot retrieve thirdparty configuration at this time</p>}
                                {!thirdparty.isLoading && !thirdparty.isError && (
                                    <table className="spark-table col-xs-12" role="grid">
                                        <thead>
                                            <tr>
                                                <th>Allowed for</th>
                                                <th>Domain</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chainDomains.length
                                                ? chainDomains.map(singleDomain => (
                                                    <tr key={singleDomain}>
                                                        <td>Chain</td>
                                                        <td>{singleDomain}</td>
                                                        <td>
                                                            <LinkIcon
                                                                iconType={chainActionsClasses}
                                                                showTooltip={true}
                                                                onClick={
                                                                    selectedLevel === SAVE_LEVEL.CHAIN &&
                                                                    this.handleRemoveDomainClick(singleDomain)
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                                : null}
                                            {hotelDomains.length
                                                ? hotelDomains.map(singleDomain => (
                                                    <tr key={singleDomain}>
                                                        <td>Hotel</td>
                                                        <td>{singleDomain}</td>
                                                        <td>
                                                            <LinkIcon
                                                                iconType="spark-icon-trash"
                                                                showTooltip={true}
                                                                onClick={this.handleRemoveDomainClick(singleDomain)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                                : null}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div role="tabpanel" className={`spark-tabs__panel ${selectedTab === "in-context" ? "active" : ""}`} id="incontext-tab">
                                <div className="allow-domain-content-header-label">
                                    <p>
                                        List the allowed domains that may host the in-context booking engine.
                                        <br />
                                        <br />
                                        Specify hosting domains by name. The domain may include an optional leading wildcard (the asterisk character, '*').
                                        <br />
                                        <br />
                                        Examples:
                                        <br />
                                        <ul>
                                            <li>
                                                &nbsp;&nbsp;<i>https://*.example.com</i>: Matches all attempts to load from any subdomain of
                                                example.com using https:.
                                            </li>
                                            <li>
                                                &nbsp;&nbsp;<i>https://scripts.example.com</i>: Matches all attempts to access scripts.example.com
                                                using https:.
                                            </li>
                                        </ul>
                                        Note that <i>*.example.com</i> does not match the top domain <i>example.com</i>. To match top domains, use
                                        <i>example.com</i> without the leading asterisk and dot (*.). Attempting to load from domains that use the insecure http protocol will result
                                        in mixed content errors, which may disrupt your site.
                                        <br />
                                        <br />
                                        The list of hosting domains is used in the booking engine Content Security Policy so that the in-context booking engine will run only on the hosting domains listed here. This security measure helps prevent the types of attacks related to clickjacking and unauthorized embedding of your site.
                                    </p>
                                </div>
                                <div className="header">
                                    <div className="group">
                                        {this.props.hasAccess && (
                                            <button
                                                className="spark-btn spark-btn--sm spark-btn--secondary"
                                                onClick={this.handleAddDomainClick}
                                            >
                                                Add domain
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {thirdparty.isLoading && <div className="ring-loader" />}
                                {thirdparty.isError && <p>Cannot retrieve thirdparty configuration at this time</p>}
                                {!thirdparty.isLoading && !thirdparty.isError && (
                                    <table className="spark-table col-xs-12" role="grid">
                                        <thead>
                                            <tr>
                                                <th>Allowed for</th>
                                                <th>Domain</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chainDomains.length
                                                ? chainDomains.map(singleDomain => (
                                                    <tr key={singleDomain}>
                                                        <td>Chain</td>
                                                        <td>{singleDomain}</td>
                                                        <td>
                                                            <LinkIcon
                                                                iconType={chainActionsClasses}
                                                                showTooltip={true}
                                                                onClick={
                                                                    selectedLevel === SAVE_LEVEL.CHAIN &&
                                                                    this.handleRemoveDomainClick(singleDomain)
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                                : null}
                                            {hotelDomains.length
                                                ? hotelDomains.map(singleDomain => (
                                                    <tr key={singleDomain}>
                                                        <td>Hotel</td>
                                                        <td>{singleDomain}</td>
                                                        <td>
                                                            <LinkIcon
                                                                iconType="spark-icon-trash"
                                                                showTooltip
                                                                onClick={this.handleRemoveDomainClick(singleDomain)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                                : null}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="allow-domain-content-header-label">
                            <p>
                                Allow Booking Engine access to the following content domains. Include your brand domain and any
                                font libraries (eg. Adobe or Google) that use scripts. Specify Internet hosts by name (or IP
                                address). The site's address may include an optional leading wildcard (the asterisk character,
                                '*').
                                <br />
                                Examples:
                                <br />
                                &nbsp;&nbsp;https://*.example.com: Matches all attempts to load from any subdomain of
                                example.com using https:.
                                <br />
                                &nbsp;&nbsp;https://scripts.example.com: Matches all attempts to access scripts.example.com
                                using https:.
                                <br />
                                <br />
                                Note that *.example.com does not match the top domain example.com. To match top domains use
                                example.com without the leading asterisk and dot.
                                <br />
                                Also note that attempting to load from domains that use the insecure http protocol will result
                                in mixed content errors, which may disrupt your site.
                            </p>
                            <PopoverEx
                                ref={node => (this.popoverRef = node)}
                                className="col-xs-4"
                                toggleEl={<i className="spark-icon-info-circle spark-icon--md" />}
                            >
                                <div className="row">
                                    <div className="col-xs-12">
                                        <h4>What's This?</h4>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12">
                                        <p>
                                            For security purposes, we block external domains not listed as "allowed". Blocked
                                            domains can effect site features that rely on calls to external domains such as
                                            custom fonts, scripts, form actions (AJAX, WebSocket and EvenSource) and external
                                            style sheets.
                                        </p>
                                    </div>
                                </div>
                            </PopoverEx>
                        </div>
                        <div className="header">
                            <h1>Allowed Domains</h1>
                            <div className="group">
                                {this.props.hasAccess && (
                                    <button
                                        className="spark-btn spark-btn--sm spark-btn--secondary"
                                        onClick={this.handleAddDomainClick}
                                    >
                                        Add domain
                                    </button>
                                )}
                            </div>
                        </div>
                        {thirdparty.isLoading && <div className="ring-loader" />}
                        {thirdparty.isError && <p>Cannot retrieve thirdparty configuration at this time</p>}
                        {!thirdparty.isLoading && !thirdparty.isError && (
                            <table className="spark-table col-xs-12" role="grid">
                                <thead>
                                    <tr>
                                        <th>Allowed for</th>
                                        <th>Domain</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chainDomains.length
                                        ? chainDomains.map(singleDomain => (
                                            <tr key={singleDomain}>
                                                <td>Chain</td>
                                                <td>{singleDomain}</td>
                                                <td>
                                                    <LinkIcon
                                                        iconType={chainActionsClasses}
                                                        showTooltip
                                                        onClick={
                                                            selectedLevel === SAVE_LEVEL.CHAIN &&
                                                            this.handleRemoveDomainClick(singleDomain)
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                        : null}
                                    {hotelDomains.length
                                        ? hotelDomains.map(singleDomain => (
                                            <tr key={singleDomain}>
                                                <td>Hotel</td>
                                                <td>{singleDomain}</td>
                                                <td>
                                                    <LinkIcon
                                                        iconType="spark-icon-trash"
                                                        showTooltip={true}
                                                        onClick={this.handleRemoveDomainClick(singleDomain)}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                        : null}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <ReactSparkModal
                    open={openAddDomainModal}
                    primaryButtonTitle="Save"
                    primaryButton={true}
                    secondaryButton={true}
                    primaryButtonClick={this.saveAddDomainModalAction}
                    secondaryButtonClick={this.cancelModalAction}
                    onClose={this.cancelModalAction}
                    secondaryButtonTitle="Cancel"
                >
                    <div>
                        <h4>Add domain</h4>
                        <div className="input-wrapper">
                            <InputTextField
                                sparkInputProps={{
                                    label: 'Domain*',
                                    errorMessage:
                                        size(domainValidationErrorCode) >= 1 &&
                                        this.getDomainErrorMessage(domainValidationErrorCode)
                                }}
                                placeholder="eg. https://www.domain.com"
                                name="theme-name"
                                value={inputData}
                                onChange={this.handleOnChangeAddDomain}
                            />
                        </div>
                    </div>
                </ReactSparkModal>

                <ReactSparkModal
                    open={this.state.openRemoveDomainModal}
                    onClose={this.cancelModalAction}
                    primaryButtonTitle="Delete"
                    primaryButton={true}
                    secondaryButton={true}
                    primaryButtonClick={this.removeDomainModalAction}
                    secondaryButtonClick={this.cancelModalAction}
                    secondaryButtonTitle="Cancel"
                >
                    <div className="add-domain-modal">
                        <h4>Delete domain</h4>
                        <div className="spark-message spark-message--lg spark-message--error spark-margin-bottom">
                            <i className="spark-message__icon spark-icon-ban spark-icon--fill"></i>
                            <div className="spark-message__content">
                                <h4 className="spark-message__heading">Are you sure you want to delete this domain?</h4>
                                <p>Some features may not function properly once this domain is deleted.</p>
                            </div>
                        </div>
                    </div>
                </ReactSparkModal>

                <ReactSparkModal
                    className="version-history"
                    primaryButtonTitle="Close"
                    primaryButton={true}
                    primaryButtonClick={this.closeVersionHistoryModalAction}
                    onClose={this.closeVersionHistoryModalAction}
                    open={this.state.openVersionHistoryModal}
                >
                    <div>
                        <h1>Version History</h1>
                        <table className="spark-table restore-modal col-xs-12" role="grid">
                            <thead>
                                <tr>
                                    <th>Date saved</th>
                                    <th>User</th>
                                    <th>Comments</th>
                                    <th>Restore</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    // TODO: version history of thirdparty
                                    /*
                                    {this.props.content.Value.map((elem, index) => {
                                        return (
                                            <ModalManageContentInfoRow
                                                key={index}
                                                showViewDetails={true}
                                                userName={elem.MetaData.User}
                                                comment={elem.MetaData.Comment}
                                                updatedTime={elem.MetaData.Timestamp}
                                                compareData={this.compareJsonFiles(index)}
                                                showDetails={() => this.showDetails(elem, index)}
                                                showRestore={index === 0 ? RESTORE_STATUS.CURRENT : this.hasAccessToRestore(contentType) ? RESTORE_STATUS.RESTORE : ''}
                                                restoreElem={() => this.restoreElem(elem, contentType)}
                                                index={index}
                                            />
                                        )
                                    }
                                    )}
                                */
                                }
                            </tbody>
                        </table>
                    </div>
                </ReactSparkModal>
            </div>
        );
    }
}

ManageDomains.propTypes = {
    onPageLoad: PropTypes.func.isRequired,
    chainId: PropTypes.string.isRequired,
    cspConfiguration: PropTypes.string.isRequired,
    hotelId: PropTypes.string.isRequired,
    selectedLevel: PropTypes.string.isRequired,
    thirdparty: PropTypes.shape({
        data: PropTypes.object.isRequired,
        isLoading: PropTypes.bool.isRequired,
        isError: PropTypes.bool.isRequired
    }).isRequired
};



// WEBPACK FOOTER //
// ./src/components/pages/manage/domains/ManageDomains/ManageDomains.js